import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo } from "react";
import { AuthContext } from "./AuthContext";
import { getUserInfo } from "../utilities/api/globalInfoService";
import { enqueueSnackbar } from "notistack";
import { UserInfo } from "../utilities/types/user/types";
import TourGuideModal from "../components/tour-guide/TourGuideModal";
import TermsAgreementModal from "../components/modal/TermsAgreementModal";
import { updateUserTourGuideStatus } from "../utilities/api/userService";
import { hasAdminAccess } from "../utilities/helper/roleHelper";

interface GlobalInfoContextType {
  userInfo: UserInfo | null;
  loading: boolean;
  isAdmin: boolean;
  refreshUserInfo: () => Promise<void>;
}

const GlobalInfoContext = createContext<GlobalInfoContextType | undefined>(undefined);

interface GlobalInfoProviderProps {
  children: ReactNode;
}

export const GlobalInfoProvider: React.FC<GlobalInfoProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTourGuide, setShowTourGuide] = useState(false);
  const [showTermsAgreement, setShowTermsAgreement] = useState(false);
  const { accessToken } = useContext(AuthContext);

  const isAdminValue = useMemo(() => hasAdminAccess(userInfo), [userInfo]);

  const fetchUserInfo = async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await getUserInfo(accessToken);

      if (response.ok) {
        setUserInfo(response.data);

        if (!response.data.tour_guide_passed) {
          setShowTourGuide(true);
        } else if (!response.data.terms) {
          // Show terms agreement modal only if tour guide is passed and terms not accepted
          setShowTermsAgreement(true);
        }
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
      }
    } catch (err: any) {
      enqueueSnackbar("Failed to retrieve user information. Please try again.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, [accessToken]);

  const refreshUserInfo = async () => {
    await fetchUserInfo();
  };

  const handleCloseTourGuide = () => {
    setShowTourGuide(false);
    updateUserTourGuideStatus(accessToken!);
    // After tour guide is closed, check if terms need to be shown
    if (userInfo && !userInfo.terms) {
      setShowTermsAgreement(true);
    }
  };

  const handleRemindLaterTerms = () => {
    setShowTermsAgreement(false);
    enqueueSnackbar("You must accept the Terms & Conditions to access services. Please go to your profile to accept it.", { variant: "warning" });
  };

  return (
    <GlobalInfoContext.Provider
      value={{
        userInfo,
        loading,
        refreshUserInfo,
        isAdmin: isAdminValue,
      }}
    >
      {children}
      <TourGuideModal isOpen={showTourGuide} onClose={handleCloseTourGuide} />
      <TermsAgreementModal isOpen={showTermsAgreement} onRemindLater={handleRemindLaterTerms} />
    </GlobalInfoContext.Provider>
  );
};

export const useGlobalInfo = () => {
  const context = useContext(GlobalInfoContext);
  if (!context) {
    throw new Error("useGlobalInfo must be used within a GlobalInfoProvider");
  }
  return context;
};
